import React, { forwardRef, useState } from 'react';
import { colors } from '../../Utils/colors';
import eye from '../../Assets/images/svg/eye.svg';
import eyeOff from '../../Assets/images/svg/eye-off.svg';

interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    id?: string;
    name: string;
    label?: string;
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
    placeholder?: string;
    value?: string | number;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    error?: string;
    touched?: boolean;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    autoComplete?: string;
    maxLength?: number;
    minLength?: number;
    pattern?: string;
    readOnly?: boolean;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'default' | 'outlined' | 'filled';
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    showPasswordToggle?: boolean;
     multiline?: boolean;
    rows?: number;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            id,
            name,
            label,
            type = 'text',
            placeholder,
            value,
            onChange,
            onBlur,
            error,
            touched,
            disabled = false,
            required = false,
            className = '',
            autoComplete,
            maxLength,
            minLength,
            pattern,
            readOnly = false,
            size = 'md',
            variant = 'default',
            icon,
            iconPosition = 'left',
            showPasswordToggle = false,
            ...props
            
        },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        const fieldId = id || name;
        const hasError = error && touched;
        const hasIcon = icon !== undefined;
        const isPasswordField = type === 'password';
        const shouldShowToggle = showPasswordToggle && isPasswordField;

        const getInputClasses = () => {
            const baseClasses = [
                'form-control',
                size === 'sm' ? 'form-control-sm' : '',
                size === 'lg' ? 'form-control-lg' : '',
                hasError ? 'is-invalid' : '',
                disabled ? 'disabled' : '',
                hasIcon ? `ps-${iconPosition === 'left' ? '5' : '3'} pe-${iconPosition === 'right' ? '5' : '3'}` : '',
                shouldShowToggle ? 'pe-5' : '',
                variant === 'outlined' ? 'border-2' : '',
                variant === 'filled' ? 'bg-light' : '',
                className
            ].filter(Boolean).join(' ');

            return baseClasses;
        };

        const handlePasswordToggle = () => {
            setShowPassword(!showPassword);
        };
        return (
            <div className="mb-3">
                                {label && (
                    <label htmlFor={fieldId} className="form-label fw-semibold text-dark text-start d-block">
                        {label}
                        {required && <span className="text-danger ms-1">*</span>}
                    </label>
                )}
                
                <div className="position-relative">
                    {hasIcon && iconPosition === 'left' && (
                        <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                            {icon}
                        </div>
                    )}
                    
                    <input
                        ref={ref}
                        id={fieldId}
                        name={name}
                        type={shouldShowToggle ? (showPassword ? 'text' : 'password') : type}
                        value={value || ''}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder={placeholder}
                        disabled={disabled}
                        required={required}
                        autoComplete={autoComplete}
                        maxLength={maxLength}
                        minLength={minLength}
                        pattern={pattern}
                        readOnly={readOnly}
                        className={getInputClasses()}
                        {...props}
                    />
                    
                    {hasIcon && iconPosition === 'right' && !shouldShowToggle && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted">
                            {icon}
                        </div>
                    )}

                    {shouldShowToggle && (
                        <button
                            type="button"
                            className="position-absolute top-50 end-0 translate-middle-y me-3 btn btn-link p-0"
                            onClick={handlePasswordToggle}
                            style={{
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                zIndex: 10,
                                color: colors.text.muted
                            }}
                            title={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {!showPassword ? <img src={eyeOff} alt="eye-off" /> : <img src={eye} alt="eye" />}
                        </button>
                    )}
                </div>
                
                {hasError && (
                    <div className="invalid-feedback d-block">
                        {error}
                    </div>
                )}
            </div>
        );
    }
);

TextField.displayName = 'TextField';

export default TextField; 